import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { DraggableModal } from '../../components/ui/DraggableModal';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import devopsBoardService, {
  Attachment,
  FieldOptions,
  WorkItemChanges,
  WorkItemComment,
  WorkItemDetail,
} from '../../services/devopsBoard.service';
import {
  appendImageTag,
  extractInlineImages,
  hasRichFormatting,
  htmlToPlainText,
  InlineImageRef,
  inlineImageTags,
  plainTextToHtml,
} from './devopsDescription';
import { attachmentTooLarge, imageFilesFromClipboard } from './newWorkItemAttachments';
import { saveAs } from '../../utils/saveFile';
import { FileType, isPreviewable, resolveFileTypeFromName } from '../../types/fileType';

/**
 * Work-item detail modal — edit a story or task without leaving the board.
 *
 * Description is edited as plain text: the app has no HTML sanitizer, so DevOps'
 * rich-text HTML is never rendered as markup. Descriptions that carry real formatting
 * are protected behind an explicit opt-in rather than being silently flattened.
 */

/** Human-readable file size; DevOps omits it on some attachments. */
function formatSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** What the lightbox is showing: a preloaded object URL, or bytes fetched on demand. */
interface PreviewTarget {
  fileName: string;
  /** Already-loaded object URL (inline thumbnails). The opener owns and revokes it. */
  src?: string;
  /** Fetches the bytes when there is no src yet (attachment rows). */
  load?: () => Promise<Blob>;
}

/**
 * Full-size viewer for an image or PDF, in the same overlay style as the Pro Docs tab.
 *
 * Escape is intercepted in the capture phase: the surrounding DraggableModal also
 * closes on a window-level Escape listener, and one keypress must not dismiss both.
 */
const PreviewLightbox: React.FC<{ target: PreviewTarget; onClose: () => void }> = ({
  target,
  onClose,
}) => {
  const [fetchedUrl, setFetchedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Unmounted when closed, so `true` is fine — unmount resets the offset.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    if (target.src || !target.load) return;
    let cancelled = false;
    let url: string | null = null;
    target
      .load()
      .then(blob => {
        url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setFetchedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(`${target.fileName} could not be loaded.`);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [target]);

  const src = target.src ?? fetchedUrl;
  const isPdf = resolveFileTypeFromName(target.fileName) === FileType.Pdf;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${target.fileName}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/60"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded bg-panel"
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader
          title={target.fileName}
          onClose={onClose}
          closeLabel="Close preview"
          dragHandleProps={dragHandleProps}
        />
        <div className="p-2">
          {error ? (
            <div className="px-8 py-12 text-center text-red-600 dark:text-red-300">{error}</div>
          ) : !src ? (
            <div className="px-8 py-12 text-center text-muted" role="status">
              Loading preview…
            </div>
          ) : isPdf ? (
            <embed src={src} type="application/pdf" width="900" height="700" />
          ) : (
            <img src={src} alt={target.fileName} className="max-h-[80vh] max-w-full object-contain" />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Screenshots pasted into a DevOps rich-text field.
 *
 * Their <img> tags point at attachment URLs the browser cannot load (they need DevOps
 * credentials), and the plain-text conversion drops them entirely — so they are pulled
 * out of the HTML and fetched through our attachment proxy instead.
 */
const InlineImages: React.FC<{
  workItemId: number;
  html: string;
  onPreview: (target: PreviewTarget) => void;
}> = ({ workItemId, html, onPreview }) => {
  const [images, setImages] = useState<{ ref: InlineImageRef; src: string }[]>([]);

  useEffect(() => {
    const refs = extractInlineImages(html);
    if (refs.length === 0) {
      setImages([]);
      return;
    }
    let cancelled = false;
    const urls: string[] = [];
    Promise.all(
      refs.map(async ref => {
        try {
          const blob = await devopsBoardService.downloadAttachment(workItemId, {
            id: ref.id,
            name: ref.fileName,
          });
          const src = URL.createObjectURL(blob);
          urls.push(src);
          return { ref, src };
        } catch {
          // An image we can't fetch simply doesn't render; the text still shows.
          return null;
        }
      }),
    ).then(loaded => {
      if (cancelled) {
        // Fetches that finished after cleanup still made URLs; releasing twice is a no-op.
        urls.forEach(u => URL.revokeObjectURL(u));
        return;
      }
      setImages(loaded.filter((x): x is { ref: InlineImageRef; src: string } => x !== null));
    });
    return () => {
      cancelled = true;
      urls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [workItemId, html]);

  if (images.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {images.map(({ ref, src }) => (
        <button
          key={ref.id}
          type="button"
          title={`Preview ${ref.fileName}`}
          onClick={() => onPreview({ fileName: ref.fileName, src })}
        >
          <img
            src={src}
            alt={ref.fileName}
            className="max-h-48 max-w-full cursor-zoom-in rounded border border-gray-200
                       dark:border-gray-700"
          />
        </button>
      ))}
    </div>
  );
};

interface Props {
  workItemId: number;
  canEdit: boolean;
  onClose: () => void;
  /** Fired after a successful save so the board can pick up the change. */
  onSaved?: () => void;
}

/** A rich-text field the modal edits. */
type HtmlField = 'description' | 'reproSteps';

/** An image pasted into a field: already uploaded to DevOps, saved on Save. */
interface PendingImage {
  field: HtmlField;
  /** The DevOps attachment URL the saved HTML will reference. */
  url: string;
  /** Local object URL for showing the thumbnail without a round-trip. */
  localUrl: string;
  fileName: string;
}

const WorkItemModal: React.FC<Props> = ({ workItemId, canEdit, onClose, onSaved }) => {
  const [item, setItem] = useState<WorkItemDetail | null>(null);
  const [options, setOptions] = useState<FieldOptions | null>(null);
  const [comments, setComments] = useState<WorkItemComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft values. Only fields that differ from the loaded item get sent.
  const [title, setTitle] = useState('');
  const [descriptionText, setDescriptionText] = useState('');
  const [state, setState] = useState('');
  const [reason, setReason] = useState('');
  const [areaPath, setAreaPath] = useState('');
  const [iterationPath, setIterationPath] = useState('');
  const [descriptionUnlocked, setDescriptionUnlocked] = useState(false);
  const [reproStepsText, setReproStepsText] = useState('');
  const [reproStepsUnlocked, setReproStepsUnlocked] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busyAttachment, setBusyAttachment] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [pastingInto, setPastingInto] = useState<HtmlField | null>(null);

  const richDescription = item ? hasRichFormatting(item.description) : false;
  const descriptionEditable = canEdit && (!richDescription || descriptionUnlocked);
  const richReproSteps = item ? hasRichFormatting(item.reproSteps) : false;
  const reproStepsEditable = canEdit && (!richReproSteps || reproStepsUnlocked);
  // Bugs carry their body in Repro Steps, so the section always shows on a Bug even
  // when empty; on other types it only appears if DevOps actually has content there.
  const showReproSteps = item ? item.type === 'Bug' || !!item.reproSteps : false;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await devopsBoardService.getWorkItem(workItemId);
      setItem(detail);
      setTitle(detail.title);
      setDescriptionText(htmlToPlainText(detail.description));
      setReproStepsText(htmlToPlainText(detail.reproSteps));
      setState(detail.state);
      setReason(detail.reason);
      setAreaPath(detail.areaPath);
      setIterationPath(detail.iterationPath);

      // Comments and dropdowns are secondary; the form still works without them.
      const [cs, opts, files] = await Promise.all([
        devopsBoardService.getComments(workItemId).catch(() => []),
        devopsBoardService.getOptions(detail.type).catch(() => null),
        devopsBoardService.getAttachments(workItemId).catch(() => []),
      ]);
      setComments(cs);
      setOptions(opts);
      setAttachments(files);
    } catch {
      setError('Could not load this work item from Azure DevOps.');
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => { load(); }, [load]);

  // Object URLs for pending thumbnails live until the modal goes away.
  const pendingRef = React.useRef<PendingImage[]>([]);
  pendingRef.current = pendingImages;
  useEffect(() => () => {
    pendingRef.current.forEach(p => URL.revokeObjectURL(p.localUrl));
  }, []);

  /**
   * Paste a screenshot straight into a field. The bytes upload immediately (unlinked,
   * like DevOps' own editor) and the <img> reference is written into the field's HTML
   * when the item is saved.
   */
  const pasteImages = async (field: HtmlField, e: React.ClipboardEvent) => {
    const files = imageFilesFromClipboard(e.clipboardData.items, pendingImages.length + 1);
    if (files.length === 0 || !canEdit) return;
    e.preventDefault();
    if (files.some(attachmentTooLarge)) {
      setError('That image is too large to attach (25 MB limit).');
      return;
    }
    setError(null);
    setPastingInto(field);
    try {
      for (const f of files) {
        const uploaded = await devopsBoardService.uploadInlineImage(workItemId, f);
        setPendingImages(list => [
          ...list,
          { field, url: uploaded.url, localUrl: URL.createObjectURL(f), fileName: f.name },
        ]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'That image could not be uploaded.');
    } finally {
      setPastingInto(null);
    }
  };

  const pendingFor = (field: HtmlField) => pendingImages.filter(p => p.field === field);

  /**
   * The HTML a field should save as, or undefined when untouched.
   *
   * A plain-text edit flattens formatting but re-appends the field's original <img>
   * tags, so screenshots survive; pasted images are appended after those.
   */
  const fieldHtml = (
    original: string, text: string, editable: boolean, field: HtmlField,
  ): string | undefined => {
    const edited = editable && text !== htmlToPlainText(original);
    const pending = pendingFor(field);
    if (!edited && pending.length === 0) return undefined;
    let html = edited
      ? plainTextToHtml(text) + inlineImageTags(original).join('')
      : original;
    for (const p of pending) html = appendImageTag(html, p.url);
    return html;
  };

  const changes: WorkItemChanges = {};
  if (item) {
    if (title !== item.title) changes.title = title;
    if (state !== item.state) changes.state = state;
    if (reason !== item.reason) changes.reason = reason;
    if (areaPath !== item.areaPath) changes.areaPath = areaPath;
    if (iterationPath !== item.iterationPath) changes.iterationPath = iterationPath;
    const desc = fieldHtml(item.description, descriptionText, descriptionEditable, 'description');
    if (desc !== undefined) changes.description = desc;
    const repro = fieldHtml(item.reproSteps, reproStepsText, reproStepsEditable, 'reproSteps');
    if (repro !== undefined) changes.reproSteps = repro;
  }
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await devopsBoardService.updateWorkItem(workItemId, changes);
      setItem(updated);
      setDescriptionText(htmlToPlainText(updated.description));
      setDescriptionUnlocked(false);
      setReproStepsText(htmlToPlainText(updated.reproSteps));
      setReproStepsUnlocked(false);
      // Saved images now come back inside the field HTML; drop the local copies.
      pendingImages.forEach(p => URL.revokeObjectURL(p.localUrl));
      setPendingImages([]);
      onSaved?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'That change could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const created = await devopsBoardService.addAttachment(workItemId, file);
      setAttachments(a => [...a, created]);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? `${file.name} could not be attached.`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (a: Attachment) => {
    setBusyAttachment(a.id);
    setError(null);
    try {
      await devopsBoardService.removeAttachment(workItemId, a.url);
      setAttachments(list => list.filter(x => x.id !== a.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? `${a.name} could not be removed.`);
    } finally {
      setBusyAttachment(null);
    }
  };

  const download = async (a: Attachment) => {
    setError(null);
    try {
      // Hand the bytes to the browser as a download rather than navigating to them.
      await saveAs(a.name, () => devopsBoardService.downloadAttachment(workItemId, a));
    } catch {
      setError(`${a.name} could not be downloaded.`);
    }
  };

  const postComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    setSaving(true);
    setError(null);
    try {
      const created = await devopsBoardService.addComment(workItemId, text);
      setComments(c => [...c, created]);
      setNewComment('');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'That note could not be posted.');
    } finally {
      setSaving(false);
    }
  };

  /** Thumbnails for images pasted into a field but not yet saved (amber border). */
  const pendingThumbs = (field: HtmlField) => {
    const pending = pendingFor(field);
    if (pending.length === 0 && pastingInto !== field) return null;
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {pending.map(p => (
          <button
            key={p.localUrl}
            type="button"
            title={`${p.fileName} — saves with the work item`}
            onClick={() => setPreview({ fileName: p.fileName, src: p.localUrl })}
          >
            <img
              src={p.localUrl}
              alt={p.fileName}
              className="max-h-48 max-w-full cursor-zoom-in rounded border-2 border-amber-400
                         dark:border-amber-500"
            />
          </button>
        ))}
        {pastingInto === field && (
          <span className="text-xs text-gray-400 dark:text-gray-500">Uploading image…</span>
        )}
      </div>
    );
  };

  const label = 'block text-xs font-medium text-muted mb-0.5';
  const field = `w-full rounded border border-gray-300 dark:border-gray-600 bg-white
                 dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100
                 disabled:opacity-60`;

  const header = (
    <span className="flex items-center gap-2">
      <span className="text-sm font-semibold">
        {item?.type ?? 'Work item'} {workItemId}
      </span>
      {item && (
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          // Stop the click reaching the header's drag handler.
          onMouseDown={e => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline
                     dark:text-sky-400"
        >
          Open in DevOps <ArrowTopRightOnSquareIcon className="h-3 w-3" />
        </a>
      )}
    </span>
  );

  return (
    <DraggableModal
      isOpen
      onClose={onClose}
      title={header}
      width={760}
      resizable
      minWidth={480}
      minHeight={360}
      // Geometry is remembered per user, so the panel reopens where they left it.
      persistKey="devops-work-item"
    >
      <div>
        {loading ? (
          <div className="p-10 text-center text-muted">Loading…</div>
        ) : (
          <div className="space-y-3 p-4">
            {error && (
              <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm
                              text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
                {error}
              </div>
            )}

            {!canEdit && (
              <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs
                              text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                Read-only. Connect your Azure DevOps token on the board to make changes.
              </div>
            )}

            <div>
              <label className={label} htmlFor="wi-title">Name</label>
              <input
                id="wi-title"
                className={field}
                value={title}
                disabled={!canEdit}
                onChange={e => setTitle(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label} htmlFor="wi-state">State</label>
                <select
                  id="wi-state"
                  className={field}
                  value={state}
                  disabled={!canEdit}
                  onChange={e => setState(e.target.value)}
                >
                  {(options?.states ?? [state]).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="wi-reason">Reason</label>
                {/* DevOps exposes no list of valid reasons through its API, so this is
                    free text rather than a dropdown that would be wrong half the time. */}
                <input
                  id="wi-reason"
                  className={field}
                  value={reason}
                  disabled={!canEdit}
                  onChange={e => setReason(e.target.value)}
                />
              </div>
              <div>
                <label className={label} htmlFor="wi-area">Area</label>
                <select
                  id="wi-area"
                  className={field}
                  value={areaPath}
                  disabled={!canEdit}
                  onChange={e => setAreaPath(e.target.value)}
                >
                  {(options?.areaPaths ?? [areaPath]).map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="wi-iteration">Iteration</label>
                <select
                  id="wi-iteration"
                  className={field}
                  value={iterationPath}
                  disabled={!canEdit}
                  onChange={e => setIterationPath(e.target.value)}
                >
                  {(options?.iterations ?? []).map(i => (
                    <option key={i.id} value={i.path}>{i.name}</option>
                  ))}
                  {/* Keep the current value selectable even if it's outside the list. */}
                  {!options?.iterations?.some(i => i.path === iterationPath) && (
                    <option value={iterationPath}>{iterationPath || '(none)'}</option>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className={label} htmlFor="wi-description">Description</label>
              {richDescription && !descriptionUnlocked && (
                <div className="mb-1 flex items-start gap-2 rounded border border-amber-300
                                bg-amber-50 px-2 py-1.5 text-xs text-amber-800
                                dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    This description has formatting (lists, links or styling) that editing
                    here would flatten to plain text. Screenshots are kept.{' '}
                    {canEdit && (
                      <button
                        className="underline"
                        onClick={() => setDescriptionUnlocked(true)}
                      >
                        Edit anyway
                      </button>
                    )}
                  </span>
                </div>
              )}
              <textarea
                id="wi-description"
                className={`${field} min-h-[7rem] font-sans`}
                value={descriptionText}
                disabled={!descriptionEditable}
                placeholder={descriptionEditable ? 'Type, or paste a screenshot (Ctrl+V / ⌘V)…' : undefined}
                onChange={e => setDescriptionText(e.target.value)}
                onPaste={e => pasteImages('description', e)}
              />
              {pendingThumbs('description')}
              {item && (
                <InlineImages
                  workItemId={workItemId}
                  html={item.description}
                  onPreview={setPreview}
                />
              )}
            </div>

            {showReproSteps && (
              <div>
                <label className={label} htmlFor="wi-repro-steps">Repro Steps</label>
                {richReproSteps && !reproStepsUnlocked && (
                  <div className="mb-1 flex items-start gap-2 rounded border border-amber-300
                                  bg-amber-50 px-2 py-1.5 text-xs text-amber-800
                                  dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      These repro steps have formatting (lists, links or styling) that
                      editing here would flatten to plain text. Screenshots are kept.{' '}
                      {canEdit && (
                        <button
                          className="underline"
                          onClick={() => setReproStepsUnlocked(true)}
                        >
                          Edit anyway
                        </button>
                      )}
                    </span>
                  </div>
                )}
                <textarea
                  id="wi-repro-steps"
                  className={`${field} min-h-[7rem] font-sans`}
                  value={reproStepsText}
                  disabled={!reproStepsEditable}
                  placeholder={reproStepsEditable ? 'Type, or paste a screenshot (Ctrl+V / ⌘V)…' : undefined}
                  onChange={e => setReproStepsText(e.target.value)}
                  onPaste={e => pasteImages('reproSteps', e)}
                />
                {pendingThumbs('reproSteps')}
                {item && (
                  <InlineImages
                    workItemId={workItemId}
                    html={item.reproSteps}
                    onPreview={setPreview}
                  />
                )}
              </div>
            )}

            {/* Attachments */}
            <div>
              <div className={label}>Attachments</div>
              <div className="space-y-1 rounded border border-gray-200 p-2 dark:border-gray-700">
                {attachments.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">No files attached.</p>
                )}
                {attachments.map(a => {
                  const canPreview = isPreviewable(resolveFileTypeFromName(a.name));
                  return (
                    <div key={a.id} className="flex items-center gap-2 text-xs">
                      <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                      <button
                        className="truncate text-blue-600 hover:underline dark:text-blue-400"
                        onClick={() =>
                          canPreview
                            ? setPreview({
                                fileName: a.name,
                                load: () => devopsBoardService.downloadAttachment(workItemId, a),
                              })
                            : download(a)
                        }
                        title={canPreview ? `Preview ${a.name}` : `Download ${a.name}`}
                      >
                        {a.name}
                      </button>
                      <span className="shrink-0 text-gray-400">{formatSize(a.size)}</span>
                      {a.comment && (
                        <span className="truncate text-muted">
                          — {a.comment}
                        </span>
                      )}
                      <button
                        className="ml-auto shrink-0 text-gray-400 hover:text-blue-600"
                        aria-label={`Download ${a.name}`}
                        onClick={() => download(a)}
                      >
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      </button>
                      {canEdit && (
                        <button
                          className="shrink-0 text-gray-400 hover:text-red-600"
                          aria-label={`Remove ${a.name}`}
                          disabled={busyAttachment === a.id}
                          onClick={() => remove(a)}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {canEdit && (
                <div className="mt-2">
                  <input
                    type="file"
                    id="wi-attach"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      // Reset the input so re-picking the same file fires onChange again.
                      e.target.value = '';
                      if (f) upload(f);
                    }}
                  />
                  <label
                    htmlFor="wi-attach"
                    className="inline-flex cursor-pointer items-center gap-1 rounded border
                               border-gray-300 px-2 py-1 text-xs text-gray-700
                               dark:border-gray-600 dark:text-gray-200"
                  >
                    <PaperClipIcon className="h-3.5 w-3.5" />
                    {uploading ? 'Uploading…' : 'Attach a file'}
                  </label>
                </div>
              )}
            </div>

            {/* Discussion */}
            <div>
              <div className={label}>Discussion</div>
              <div className="max-h-52 space-y-2 overflow-y-auto rounded border
                              border-gray-200 p-2 dark:border-gray-700">
                {comments.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">No notes yet.</p>
                )}
                {comments.map(c => (
                  <div key={c.id} className="text-xs">
                    <div className="font-medium text-gray-700 dark:text-gray-200">
                      {c.createdBy ?? 'Unknown'}
                      <span className="ml-2 font-normal text-gray-400">
                        {c.createdDate ? new Date(c.createdDate).toLocaleString() : ''}
                      </span>
                    </div>
                    {/* Comment bodies are HTML too, and are shown as text for the same
                        reason the description is. */}
                    <div className="whitespace-pre-wrap text-gray-600 dark:text-gray-300">
                      {htmlToPlainText(c.text)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  className={field}
                  placeholder="Add a note…"
                  value={newComment}
                  disabled={!canEdit}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') postComment(); }}
                />
                <button
                  className="shrink-0 rounded border border-gray-300 px-3 py-1 text-sm
                             text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
                  disabled={!canEdit || saving || !newComment.trim()}
                  onClick={postComment}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
              {dirty && (
                <span className="mr-auto text-xs text-muted">
                  {Object.keys(changes).length} unsaved change
                  {Object.keys(changes).length === 1 ? '' : 's'}
                </span>
              )}
              <button
                className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700
                           dark:border-gray-600 dark:text-gray-200"
                onClick={onClose}
              >
                Close
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                disabled={!canEdit || !dirty || saving}
                onClick={save}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
        {preview && <PreviewLightbox target={preview} onClose={() => setPreview(null)} />}
      </div>
    </DraggableModal>
  );
};

export default WorkItemModal;
